import React from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import CommentSection from "../CommentSection";

export default function StandardFileViewerLayout({
    file,
    folder,
    uploadedBy,
    userHasPermission,
    media,
    mediaBackground = "background.media",
    mediaPadding = 0,
    onBack,
    onFavourite,
    onMore,
    isInFavourites,
    primaryActions,
    moreActionsLabel = "More file actions",
}) {
    if (userHasPermission === undefined) {
        return (
            <Box
                sx={{
                    minHeight: {
                        xs: "calc(100dvh - 60px)",
                        sm: "calc(100vh - 128px)",
                    },
                    backgroundColor: "background.default",
                    pt: { xs: 2, md: 3 },
                    pb: 10,
                }}
            >
                <Container
                    maxWidth={false}
                    sx={{ maxWidth: "1520px", px: { xs: 1.5, sm: 3 } }}
                >
                    <Skeleton
                        variant="rounded"
                        width={112}
                        height={34}
                        sx={{ mb: 1.5, borderRadius: 2 }}
                    />
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "minmax(0, 1fr)",
                                md: "minmax(0, 1.8fr) minmax(320px, 0.8fr)",
                            },
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Skeleton
                                variant="rounded"
                                width="100%"
                                height={360}
                                sx={{ borderRadius: 3 }}
                            />
                            <Skeleton
                                variant="text"
                                width="58%"
                                height={48}
                                sx={{ mt: 1.5 }}
                            />
                            <Skeleton variant="text" width="34%" height={26} />
                        </Box>
                        <Skeleton
                            variant="rounded"
                            width="100%"
                            height={420}
                            sx={{ borderRadius: 3 }}
                        />
                    </Box>
                </Container>
            </Box>
        );
    }

    if (!userHasPermission) {
        return null;
    }

    return (
        <Box
            sx={{
                minHeight: {
                    xs: "calc(100dvh - 60px)",
                    sm: "calc(100vh - 128px)",
                },
                backgroundColor: "background.default",
                pt: { xs: 1.5, sm: 2.5, md: 3 },
                pb: 10,
            }}
        >
            <Container
                maxWidth={false}
                sx={{ maxWidth: "1520px", px: { xs: 1.5, sm: 3 } }}
            >
                <Button
                    color="inherit"
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{ mb: 1.5, color: "text.secondary" }}
                >
                    Back to Files
                </Button>

                {file && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "minmax(0, 1fr)",
                                md: "minmax(0, 1.8fr) minmax(320px, 0.8fr)",
                            },
                            gap: 2,
                            alignItems: "start",
                        }}
                    >
                        <Card
                            variant="outlined"
                            sx={{
                                minWidth: 0,
                                overflow: "hidden",
                                borderRadius: 3,
                                borderColor: "divider",
                                backgroundColor: "background.paper",
                                boxShadow:
                                    "0 6px 24px rgba(20, 35, 25, 0.07)",
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor: mediaBackground,
                                    p: mediaPadding,
                                }}
                            >
                                {media}
                            </Box>

                            <CardContent
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    "&:last-child": {
                                        pb: { xs: 2, sm: 3 },
                                    },
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    component="h1"
                                    sx={{
                                        overflowWrap: "break-word",
                                        lineHeight: 1.2,
                                        fontSize: { xs: 24, sm: 30 },
                                    }}
                                >
                                    {file.displayName}
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 1.25,
                                        mt: 1,
                                        color: "text.secondary",
                                    }}
                                >
                                    {folder?.name && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                            }}
                                        >
                                            <FolderOutlinedIcon
                                                sx={{ fontSize: 17 }}
                                            />
                                            <Typography variant="body2">
                                                {folder.name}
                                            </Typography>
                                        </Box>
                                    )}
                                    {uploadedBy && (
                                        <Typography variant="body2">
                                            Uploaded by{" "}
                                            <Box
                                                component="span"
                                                sx={{
                                                    color: "text.primary",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {uploadedBy}
                                            </Box>
                                        </Typography>
                                    )}
                                </Box>

                                <Divider sx={{ my: 2.5 }} />

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 1,
                                    }}
                                >
                                    {primaryActions}
                                    <Button
                                        variant={
                                            isInFavourites
                                                ? "contained"
                                                : "outlined"
                                        }
                                        size="small"
                                        startIcon={
                                            isInFavourites ? (
                                                <StarIcon />
                                            ) : (
                                                <StarBorderIcon />
                                            )
                                        }
                                        onClick={onFavourite}
                                        sx={{
                                            flex: {
                                                xs: "1 1 calc(50% - 4px)",
                                                sm: "0 0 auto",
                                            },
                                        }}
                                    >
                                        {isInFavourites
                                            ? "Favourited"
                                            : "Favourite"}
                                    </Button>
                                    <Tooltip title="More actions">
                                        <IconButton
                                            onClick={onMore}
                                            aria-label={moreActionsLabel}
                                            sx={{
                                                ml: "auto",
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 2,
                                            }}
                                        >
                                            <MoreHorizIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </CardContent>
                        </Card>

                        <Card
                            variant="outlined"
                            sx={{
                                borderRadius: 3,
                                borderColor: "divider",
                                backgroundColor: "background.paper",
                                boxShadow:
                                    "0 2px 10px rgba(20, 35, 25, 0.045)",
                                position: { md: "sticky" },
                                top: { md: 80 },
                                overflow: "hidden",
                            }}
                        >
                            {file.allowComments ? (
                                <CommentSection file={file} modern />
                            ) : (
                                <Box
                                    sx={{
                                        minHeight: 260,
                                        p: 3,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center",
                                    }}
                                >
                                    <ForumOutlinedIcon
                                        sx={{
                                            fontSize: 38,
                                            mb: 1.25,
                                            color: "text.disabled",
                                        }}
                                    />
                                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                                        Comments unavailable
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "text.secondary" }}
                                    >
                                        Comments are disabled for this file.
                                    </Typography>
                                </Box>
                            )}
                        </Card>
                    </Box>
                )}
            </Container>
        </Box>
    );
}
